import {
  ElementType,
  Element as IElement,
  isRectLikeElement,
  Polyline,
  Rect,
} from '@ritabygglov/core';
import {
  AppState,
  Coordinate,
  LineSegment,
  SnapCandidate,
  SnapLine,
  Vector,
} from '../types';
import { SnapMetrics } from './snapScale';
import {
  SNAP_CANDIDATE_RADIUS_RATIO,
  SNAP_LINE_LINE_RATIO,
  SNAP_MATCH_EPSILON_RATIO,
} from './snapTokens';
import {
  getElementAxisAlignedCorners,
  getElementShapeEdges,
  getElementVertices,
  getThicknessFaces,
  hasSnappableFaces,
  rotatePointAroundCenter,
} from './utility';

/**
 * Creates horizontal and vertical snap lines through a center point,
 * rotated by the given angle
 */
const createCenterSnapLines = (
  center: Coordinate,
  rotation: number,
  extensionDistance: number
): SnapLine[] => {
  const horizontalP1 = rotatePointAroundCenter(
    { x: center.x - 1, y: center.y },
    center,
    rotation
  );
  const horizontalP2 = rotatePointAroundCenter(
    { x: center.x + 1, y: center.y },
    center,
    rotation
  );
  const horizontalLine: SnapLine = {
    original: { start: horizontalP1, end: horizontalP2 },
    extended: extendLineSegment(horizontalP1, horizontalP2, extensionDistance),
  };

  const verticalP1 = rotatePointAroundCenter(
    { x: center.x, y: center.y - 1 },
    center,
    rotation
  );
  const verticalP2 = rotatePointAroundCenter(
    { x: center.x, y: center.y + 1 },
    center,
    rotation
  );
  const verticalLine: SnapLine = {
    original: { start: verticalP1, end: verticalP2 },
    extended: extendLineSegment(verticalP1, verticalP2, extensionDistance),
  };

  return [horizontalLine, verticalLine];
};

/**
 * A single segment shifted sideways by `distance` along its normal, as a snap
 * line. Returns nothing for a zero-length segment or a zero offset (the latter
 * would duplicate the geometry line the caller already emitted).
 */
const offsetSegment = (
  start: Coordinate,
  end: Coordinate,
  distance: number,
  extensionDistance: number
): SnapLine[] => {
  if (distance === 0) return [];

  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length === 0) return [];

  const normalX = -dy / length;
  const normalY = dx / length;

  const offsetStart = {
    x: start.x + normalX * distance,
    y: start.y + normalY * distance,
  };
  const offsetEnd = {
    x: end.x + normalX * distance,
    y: end.y + normalY * distance,
  };

  return [
    {
      original: { start: offsetStart, end: offsetEnd },
      extended: extendLineSegment(offsetStart, offsetEnd, extensionDistance),
    },
  ];
};

const isRotatableElementType = (element: IElement): boolean =>
  element.type === ElementType.Rect ||
  element.type === ElementType.Text ||
  element.type === ElementType.Trapezoid ||
  element.type === ElementType.Triangle ||
  element.type === ElementType.Angle ||
  element.type === ElementType.Fixture ||
  element.type === ElementType.Group;

const createSnapLinesFromElement = (
  element: IElement,
  extensionDistance: number
): SnapLine[] => {
  const snaplines: SnapLine[] = [];

  if (element.type === ElementType.Polyline) {
    const polyline = element as Polyline;
    const faces = hasSnappableFaces(polyline)
      ? getThicknessFaces(polyline)
      : null;

    for (let i = 0; i < polyline.points.length - 1; i++) {
      const start = polyline.points[i];
      const end = polyline.points[i + 1];
      snaplines.push({
        original: { start, end },
        extended: extendLineSegment(start, end, extensionDistance),
      });

      if (faces) {
        // Each segment is offset on its own: a polyline is not convex, so
        // mitring the corners would be wrong, and snapping does not need it.
        snaplines.push(
          ...offsetSegment(start, end, faces.inner, extensionDistance),
          ...offsetSegment(start, end, faces.outer, extensionDistance)
        );
      }
    }
    return snaplines;
  }

  const rect = element as Rect;
  const center: Coordinate = {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
  const rotation = isRotatableElementType(element) ? rect.rotate || 0 : 0;

  //crate snaplines extending from the center of the element
  snaplines.push(...createCenterSnapLines(center, rotation, extensionDistance));

  if (element.type === ElementType.Symbol) {
    return snaplines;
  }

  if (element.type === ElementType.Group) {
    const corners = getElementAxisAlignedCorners(element);
    for (let i = 0; i < corners.length; i++) {
      const [sx, sy] = corners[i];
      const [ex, ey] = corners[(i + 1) % corners.length];
      const start: Coordinate = { x: sx, y: sy };
      const end: Coordinate = { x: ex, y: ey };
      snaplines.push({
        original: { start, end },
        extended: extendLineSegment(start, end, extensionDistance),
      });
    }
    return snaplines;
  }

  if (!isRectLikeElement(element)) {
    return snaplines;
  }

  const edges = getElementShapeEdges(element);

  for (const { start, end } of edges) {
    const lineStart = rotation
      ? rotatePointAroundCenter(start, center, rotation)
      : start;
    const lineEnd = rotation
      ? rotatePointAroundCenter(end, center, rotation)
      : end;
    snaplines.push({
      original: { start: lineStart, end: lineEnd },
      extended: extendLineSegment(lineStart, lineEnd, extensionDistance),
    });
  }

  return snaplines;
};

/**
 * Extend line segment outwards with extensiondistance in both directions
 */
const extendLineSegment = (
  start: Coordinate,
  end: Coordinate,
  extensionDistance: number
): LineSegment => {
  // Calculate direction vector
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  // Calculate length
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length === 0) {
    return { start, end }; // Can't extend a point
  }

  // Normalize direction
  const unitX = dx / length;
  const unitY = dy / length;

  // Extend in both directions
  return {
    start: {
      x: start.x - unitX * extensionDistance,
      y: start.y - unitY * extensionDistance,
    },
    end: {
      x: end.x + unitX * extensionDistance,
      y: end.y + unitY * extensionDistance,
    },
  };
};

/**
 * Caluclate shortest distance to line segment from given coordinate.
 * Returns number of shortest distance.
 */
const distanceToLineSegment = (
  point: Coordinate,
  segment: LineSegment
): number => {
  const { start, end } = segment;
  const A = end.x - start.x;
  const B = end.y - start.y;
  const C = start.x - point.x;
  const D = start.y - point.y;

  const dot = A * C + B * D;
  const lenSq = A * A + B * B;

  if (lenSq === 0) {
    // Line start and end are the same point
    return Math.sqrt(C * C + D * D);
  }

  let param = -dot / lenSq;

  // Clamp parameter to [0, 1] to stay within the segment
  param = Math.max(0, Math.min(1, param));

  // Find the closest point on the segment
  const closestX = start.x + param * A;
  const closestY = start.y + param * B;

  // Return distance to that point
  const dx = point.x - closestX;
  const dy = point.y - closestY;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Find intersection point of two lines (extended infinitely).
 * Returns null if lines are parallel or coincident.
 */
const findLineLineIntersection = (
  line1: LineSegment,
  line2: LineSegment
): Coordinate | null => {
  const x1 = line1.start.x,
    y1 = line1.start.y;
  const x2 = line1.end.x,
    y2 = line1.end.y;
  const x3 = line2.start.x,
    y3 = line2.start.y;
  const x4 = line2.end.x,
    y4 = line2.end.y;

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

  if (Math.abs(denom) < 0.0001) {
    return null;
  }

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;

  return {
    x: x1 + t * (x2 - x1),
    y: y1 + t * (y2 - y1),
  };
};

/**
 * Caluclate closest point on line segment from given coordinate.
 * Returns coordinate (x,y) of point along line segment.
 */
const closestPointOnLineSegment = (
  point: Coordinate,
  segment: LineSegment
): Coordinate => {
  const { start, end } = segment;
  const A = end.x - start.x;
  const B = end.y - start.y;
  const C = start.x - point.x;
  const D = start.y - point.y;

  const dot = A * C + B * D;
  const lenSq = A * A + B * B;

  if (lenSq === 0) {
    return { ...start };
  }

  let param = -dot / lenSq;
  param = Math.max(0, Math.min(1, param));

  return {
    x: start.x + param * A,
    y: start.y + param * B,
  };
};

/**
 * Returns the set of snap-relevant coordinates for an element:
 * its vertices plus (for rect-like shapes) its center, rotated if applicable.
 */
const getElementCoordinates = (element: IElement): Coordinate[] => {
  if (element.type === ElementType.Polyline) {
    return (element as Polyline).points;
  }

  if (element.type === ElementType.Symbol) {
    return [
      { x: element.x + element.width / 2, y: element.y + element.height / 2 },
    ];
  }

  if (element.type === ElementType.Group) {
    const center: Coordinate = {
      x: element.x + element.width / 2,
      y: element.y + element.height / 2,
    };
    const corners = getElementAxisAlignedCorners(element).map(
      ([x, y]): Coordinate => ({ x, y })
    );
    return [...corners, center];
  }

  if (
    element.type !== ElementType.Rect &&
    element.type !== ElementType.Text &&
    element.type !== ElementType.Triangle &&
    element.type !== ElementType.Trapezoid &&
    element.type !== ElementType.Angle &&
    element.type !== ElementType.Fixture
  ) {
    return [];
  }

  const rect = element as Rect;
  const center: Coordinate = {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };

  let points: Coordinate[] = [...getElementVertices(element), center];

  if (rect.rotate && rect.rotate !== 0) {
    points = points.map((point) =>
      rotatePointAroundCenter(point, center, rect.rotate)
    );
  }

  return points;
};

/**
 * Creates snaplines and snap points for inputed elements
 * exludes the originelement from snap point creation
 */
export const getOrCreateSnapLines = (
  metrics: SnapMetrics,
  allElements: AppState['elements'],
  originElementId?: string
): { snapLines: SnapLine[]; snapPoints: Coordinate[] } => {
  //here we could fetch cached lines later

  const snapLines: SnapLine[] = [];
  const snapPoints: Coordinate[] = [];

  // for polylines, we could inlude the element being created but not the current point
  // to make it snap to itself.
  Object.values(allElements).forEach((element) => {
    // Exclude selected element
    if (element.id !== originElementId) {
      // Collect snap points
      const elementPoints = getElementCoordinates(element);
      snapPoints.push(...elementPoints);

      // Create snap lines
      const elementSnapLines = createSnapLinesFromElement(
        element,
        metrics.extensionDistance
      );
      snapLines.push(...elementSnapLines);
    }
  });

  return { snapLines, snapPoints };
};

/**
 * Given two snap candidates, find a point that satisfies both constraints.
 * Handles four cases: (point, point), (line, point), (point, line), (line, line)
 * Returns the intersection point or null if constraints can't be satisfied.
 */
const findDualSnapIntersection = (
  candidate1: SnapCandidate,
  candidate2: SnapCandidate,
  tolerance: number
): Coordinate | null => {
  const isLine1 = candidate1.sourceLine !== undefined;
  const isLine2 = candidate2.sourceLine !== undefined;

  if (!isLine1 && !isLine2) {
    return candidate1.targetPoint;
  }

  if (isLine1 && !isLine2) {
    // (line, point): Project point onto line and check distance
    const projectedPoint = closestPointOnLineSegment(
      candidate2.targetPoint,
      candidate1.sourceLine!
    );
    const dist = distanceToLineSegment(
      candidate2.targetPoint,
      candidate1.sourceLine!
    );

    if (dist < tolerance) {
      return projectedPoint;
    }
    return null;
  }

  if (!isLine1 && isLine2) {
    // (point, line): Project point onto line and check distance
    const projectedPoint = closestPointOnLineSegment(
      candidate1.targetPoint,
      candidate2.sourceLine!
    );
    const dist = distanceToLineSegment(
      candidate1.targetPoint,
      candidate2.sourceLine!
    );

    if (dist < tolerance) {
      return projectedPoint;
    }
    return null;
  }

  // (line, line): Find geometric intersection
  const intersection = findLineLineIntersection(
    candidate1.sourceLine!,
    candidate2.sourceLine!
  );

  if (!intersection) {
    // check whether both candidates can be satisfied with the same delta?
    const deltaX =
      candidate1.targetPoint.x -
      candidate1.originPoint.x -
      (candidate2.targetPoint.x - candidate2.originPoint.x);
    const deltaY =
      candidate1.targetPoint.y -
      candidate1.originPoint.y -
      (candidate2.targetPoint.y - candidate2.originPoint.y);

    if (deltaX === 0 && deltaY === 0) {
      return candidate1.targetPoint;
    }

    return null;
  }

  // Check that intersection is within reasonable distance of both original target points
  const dist1 = Math.sqrt(
    Math.pow(intersection.x - candidate1.targetPoint.x, 2) +
      Math.pow(intersection.y - candidate1.targetPoint.y, 2)
  );
  const dist2 = Math.sqrt(
    Math.pow(intersection.x - candidate2.targetPoint.x, 2) +
      Math.pow(intersection.y - candidate2.targetPoint.y, 2)
  );

  // Allow a larger tolerance for line-line intersections since the snap
  // will be visually correct even if the intersection is further away
  const lineLineTolerance = tolerance * SNAP_LINE_LINE_RATIO;
  if (dist1 < lineLineTolerance && dist2 < lineLineTolerance) {
    return intersection;
  }

  return null;
};

/**
 * Find up to two best candidates to snap to.
 */
const findBestSnapCandidates = (
  metrics: SnapMetrics,
  originPoints: Coordinate[],
  deltaX: number,
  deltaY: number,
  snapLines: SnapLine[],
  snapPoints: Coordinate[],
  freeAxis?: Coordinate
) => {
  //candidate inclusion distance is larger than actual snap distance
  const renderDistance = metrics.tolerance * SNAP_CANDIDATE_RADIUS_RATIO;

  //find best and second best point for snaping
  let bestCandidate: SnapCandidate | undefined = undefined;
  let secondCandidate: SnapCandidate | undefined = undefined;

  for (const snapPoint of snapPoints) {
    for (const point of originPoints) {
      //check all points of selected element
      const currentPosition: Coordinate = {
        x: point.x + deltaX,
        y: point.y + deltaY,
      };

      //calculate distance
      const dx = currentPosition.x - snapPoint.x;
      const dy = currentPosition.y - snapPoint.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < renderDistance) {
        // new better candidate found

        const targetPoint = snapPoint;
        const candidate: SnapCandidate = {
          targetPoint: targetPoint,
          originPoint: point,
          distance: distance,
        };

        // Rank the candidate
        if (!bestCandidate || distance < bestCandidate.distance) {
          // New best candidate found - demote current best to second
          secondCandidate = bestCandidate;
          bestCandidate = candidate;
        } else if (!secondCandidate || distance < secondCandidate.distance) {
          // New second-best candidate found
          secondCandidate = candidate;
        }
      }
    }
  }

  //check lines
  for (const line of snapLines) {
    //iterate through snapLines
    for (const point of originPoints) {
      //check all points of selected element
      const currentPosition: Coordinate = {
        x: point.x + deltaX,
        y: point.y + deltaY,
      };
      let distance: number;
      let targetPoint: Coordinate;

      if (freeAxis) {
        // axis to track snapping along (e.g. when rotated edge drag)
        const track: LineSegment = {
          start: currentPosition,
          end: {
            x: currentPosition.x + freeAxis.x,
            y: currentPosition.y + freeAxis.y,
          },
        };
        const intersection = findLineLineIntersection(line.extended, track);
        if (!intersection) continue;
        if (distanceToLineSegment(intersection, line.extended) > 0.01) {
          continue;
        }
        const trackDx = intersection.x - currentPosition.x;
        const trackDy = intersection.y - currentPosition.y;
        distance = Math.sqrt(trackDx * trackDx + trackDy * trackDy);
        targetPoint = intersection;
      } else {
        // add penalty to lines to prioritse points otherwise they're overwritten
        // can't add penalty when snapping along a free axis
        distance =
          distanceToLineSegment(currentPosition, line.extended) +
          metrics.linePenalty;
        targetPoint = closestPointOnLineSegment(currentPosition, line.extended);
      }

      if (distance < renderDistance) {
        // new better candidate found
        const candidate: SnapCandidate = {
          targetPoint: targetPoint,
          originPoint: point,
          distance: distance,
          sourceLine: line.extended,
        };

        // Rank the candidate
        if (!bestCandidate || distance < bestCandidate.distance) {
          // New best candidate found - demote current best to second
          secondCandidate = bestCandidate;
          bestCandidate = candidate;
        } else if (!secondCandidate || distance < secondCandidate.distance) {
          // New second-best candidate found
          secondCandidate = candidate;
        }
      }
    }
  }

  return {
    bestCandidate,
    secondCandidate,
  };
};

/**
 * Given a finalized snap position, find every snap line and snap point
 * that the active element's origin points currently lie on.
 * Used purely for rendering — decoupled from the best/second position logic.
 */
const findAllMatchesAtPosition = (
  metrics: SnapMetrics,
  originPoints: Coordinate[],
  finalDx: number,
  finalDy: number,
  snapLines: SnapLine[],
  snapPoints: Coordinate[]
): { matchedLines: LineSegment[]; matchedPoints: Coordinate[] } => {
  const epsilon = metrics.tolerance * SNAP_MATCH_EPSILON_RATIO;
  const matchedLines: LineSegment[] = [];
  const matchedPoints: Coordinate[] = [];

  for (const snapPoint of snapPoints) {
    for (const point of originPoints) {
      const dx = point.x + finalDx - snapPoint.x;
      const dy = point.y + finalDy - snapPoint.y;
      if (Math.sqrt(dx * dx + dy * dy) < epsilon) {
        matchedPoints.push(snapPoint);
        break;
      }
    }
  }

  for (const line of snapLines) {
    let bestSegment: LineSegment | undefined;
    let bestLength = Infinity;

    for (const point of originPoints) {
      const activePoint: Coordinate = {
        x: point.x + finalDx,
        y: point.y + finalDy,
      };
      if (distanceToLineSegment(activePoint, line.extended) < epsilon) {
        // Visualize from the active point to the closest point on the
        // reference element's original (unextended) edge.
        const referencePoint = closestPointOnLineSegment(
          activePoint,
          line.original
        );
        const dx = activePoint.x - referencePoint.x;
        const dy = activePoint.y - referencePoint.y;

        const segmentLength = Math.sqrt(dx * dx + dy * dy);
        if (segmentLength < epsilon) {
          // Active point lies directly on the reference edge, so there is no
          // offset connector to draw. Render the edge itself so the snap
          // guideline is still visible — but only when the point falls between
          // the edge's endpoints. If it coincides with an endpoint it's a
          // corner snap point, already shown as a point marker.
          const sx = activePoint.x - line.original.start.x;
          const sy = activePoint.y - line.original.start.y;
          const ex = activePoint.x - line.original.end.x;
          const ey = activePoint.y - line.original.end.y;
          const atEndpoint =
            Math.sqrt(sx * sx + sy * sy) < epsilon ||
            Math.sqrt(ex * ex + ey * ey) < epsilon;
          if (!atEndpoint) {
            bestSegment = line.original;
            bestLength = 0;
          }
        } else if (segmentLength < bestLength) {
          bestLength = segmentLength;
          bestSegment = { start: activePoint, end: referencePoint };
        }
      }
    }
    if (bestSegment) {
      matchedLines.push(bestSegment);
    }
  }

  return { matchedLines, matchedPoints };
};

/**
 * Runs the existing match pass at an arbitrary point without re-running
 * snap selection. Used after a secondary constraint (e.g. shift-based
 * angle lock) has moved the endpoint away from the element-snap position,
 * so the rendered snaplines reflect the final point.
 */
export const findMatchesAtPoint = (
  metrics: SnapMetrics,
  point: Coordinate,
  elements: AppState['elements'],
  excludeElementId?: string
): { matchedLines: LineSegment[]; matchedPoints: Coordinate[] } => {
  const { snapLines, snapPoints } = getOrCreateSnapLines(
    metrics,
    elements,
    excludeElementId
  );
  return findAllMatchesAtPosition(
    metrics,
    [{ x: 0, y: 0 }],
    point.x,
    point.y,
    snapLines,
    snapPoints
  );
};

/**
 * Calculates snapped position for entire element or single point
 * The selected element is filtered out from snapline creation
 * Returns snapped position and every snap line/point currently aligned
 * with the active element at that position
 */
export const snapToElementsOnMove = (
  metrics: SnapMetrics,
  deltaX: number,
  deltaY: number,
  elementsToSnapTo: AppState['elements'],
  snappingSinglePoint: boolean,
  activeElement?: IElement
): {
  dx: number;
  dy: number;
  matchedLines: LineSegment[];
  matchedPoints: Coordinate[];
} => {
  let originPoints: Coordinate[] = [{ x: 0, y: 0 }];

  const emptyMatches = { matchedLines: [], matchedPoints: [] };

  if (!activeElement && !snappingSinglePoint) {
    return { dx: deltaX, dy: deltaY, ...emptyMatches };
  }

  if (activeElement && !snappingSinglePoint) {
    originPoints = getElementCoordinates(activeElement);
  }

  if (originPoints.length === 0)
    return { dx: deltaX, dy: deltaY, ...emptyMatches };

  const { snapLines, snapPoints } = activeElement
    ? getOrCreateSnapLines(metrics, elementsToSnapTo, activeElement.id)
    : getOrCreateSnapLines(metrics, elementsToSnapTo);

  const { bestCandidate, secondCandidate } = findBestSnapCandidates(
    metrics,
    originPoints,
    deltaX,
    deltaY,
    snapLines,
    snapPoints
  );

  let finalDx = deltaX;
  let finalDy = deltaY;

  const bestWithinTolerance =
    bestCandidate && bestCandidate.distance < metrics.tolerance;
  const secondWithinTolerance =
    secondCandidate && secondCandidate.distance < metrics.tolerance;

  let snapEngaged = false;

  if (bestWithinTolerance && secondWithinTolerance) {
    const intersectionPoint = findDualSnapIntersection(
      bestCandidate,
      secondCandidate,
      metrics.tolerance
    );

    if (intersectionPoint) {
      finalDx = intersectionPoint.x - bestCandidate.originPoint.x;
      finalDy = intersectionPoint.y - bestCandidate.originPoint.y;
    } else {
      finalDx = bestCandidate.targetPoint.x - bestCandidate.originPoint.x;
      finalDy = bestCandidate.targetPoint.y - bestCandidate.originPoint.y;
    }
    snapEngaged = true;
  } else if (bestWithinTolerance) {
    finalDx = bestCandidate.targetPoint.x - bestCandidate.originPoint.x;
    finalDy = bestCandidate.targetPoint.y - bestCandidate.originPoint.y;
    snapEngaged = true;
  } else if (secondWithinTolerance) {
    finalDx = secondCandidate.targetPoint.x - secondCandidate.originPoint.x;
    finalDy = secondCandidate.targetPoint.y - secondCandidate.originPoint.y;
    snapEngaged = true;
  }

  if (!snapEngaged) {
    return { dx: finalDx, dy: finalDy, ...emptyMatches };
  }

  const { matchedLines, matchedPoints } = findAllMatchesAtPosition(
    metrics,
    originPoints,
    finalDx,
    finalDy,
    snapLines,
    snapPoints
  );

  return {
    dx: finalDx,
    dy: finalDy,
    matchedLines,
    matchedPoints,
  };
};

/**
 * Calculates a snapped position for an element edge drag along,
 * one free axis given as a unit direction vector (the world-space
 * direction the edge moves in, e.g. the element's rotated local axis).
 * Similar to snapToElementOnMove
 */
export const snapToElementsOnEdgeDrag = (
  metrics: SnapMetrics,
  deltaX: number,
  deltaY: number,
  freeAxis: Vector,
  edgePoints: Coordinate[],
  elementsToSnapTo: AppState['elements'],
  activeElementId: string
): {
  dx: number;
  dy: number;
  matchedLines: LineSegment[];
  matchedPoints: Coordinate[];
} => {
  if (edgePoints.length === 0) {
    return { dx: deltaX, dy: deltaY, matchedLines: [], matchedPoints: [] };
  }

  const { snapLines, snapPoints } = getOrCreateSnapLines(
    metrics,
    elementsToSnapTo,
    activeElementId
  );

  const { bestCandidate } = findBestSnapCandidates(
    metrics,
    edgePoints,
    deltaX,
    deltaY,
    snapLines,
    [],
    freeAxis
  );

  if (!bestCandidate || bestCandidate.distance >= metrics.tolerance) {
    return { dx: deltaX, dy: deltaY, matchedLines: [], matchedPoints: [] };
  }

  const currentPosition: Coordinate = {
    x: bestCandidate.originPoint.x + deltaX,
    y: bestCandidate.originPoint.y + deltaY,
  };
  const correction =
    (bestCandidate.targetPoint.x - currentPosition.x) * freeAxis.x +
    (bestCandidate.targetPoint.y - currentPosition.y) * freeAxis.y;
  const finalDx = deltaX + correction * freeAxis.x;
  const finalDy = deltaY + correction * freeAxis.y;

  const { matchedLines, matchedPoints } = findAllMatchesAtPosition(
    metrics,
    edgePoints,
    finalDx,
    finalDy,
    snapLines,
    snapPoints
  );

  return { dx: finalDx, dy: finalDy, matchedLines, matchedPoints };
};
